// TypeScript type definitions based on StcBinDTOs.cs

export interface RackSummary {
    rackNumber: string;
    locationCount: number;
    minVertical?: string | null;
    maxVertical?: string | null;
    minShelf?: string | null;
    maxShelf?: string | null;
    distinctBinSizeNo: number[];
    distinctBinTypeNo: number[];
}

export interface BinLocation {
    binCode: string;
    description1?: string;
    binSizeNo: number;
    binTypeNo: number;
    resourceNo?: number;
    stcZoneID?: string;
    binGrpNo?: number;
    stcBinCatNo?: number;
    pickPriorityNo?: number;
    pickableYesNo?: boolean;
    reservationAllowed?: boolean;
    autoReplenishment?: boolean;
    itemNo?: string;

    // Extended properties
    replenishMinLevel?: number;
    replenishMaxLevel?: number;
    customSortID?: number;
    putAwayPriorityNo?: number;
    negativeReservationAllowed?: boolean;
    sharedBinYesNo?: boolean;
    sharedBinMaxQuantity?: number;
    sharedBinPreferEmptyBin?: boolean;

    // Parsed dimensions (strenger for å støtte både numerisk og alfanumerisk format)
    rackNumber: string;
    vertical: string;
    shelf: string;
    drawer?: string;
}

/**
 * Groups multiple BinLocations that share the same rack/vertical/shelf coordinates.
 * Used for displaying 4D racks where multiple drawers exist in a single grid cell.
 */
export interface BinLocationGroup {
    /** Key for this group: "rackNumber-vertical-shelf" */
    cellKey: string;
    rackNumber: string;
    vertical: string;
    shelf: string;
    /** All locations (drawers) in this cell */
    locations: BinLocation[];
    /** The first location, used for display in the grid */
    primaryLocation: BinLocation;
    /** True if this cell has multiple drawers */
    hasMultipleDrawers: boolean;
    /** Number of drawers in this cell */
    drawerCount: number;
}

export interface RackStructure {
    rackNumber: string;
    locations: BinLocation[];
    verticals: string[];
    shelves: string[];
    gridWidth: number;
    gridHeight: number;
}

/**
 * Natural sort comparator for segment-koder. Numerisk hvis begge er parsbare som heltall,
 * ellers alfabetisk (case-insensitiv).
 */
export const compareSegments = (a: string, b: string): number => {
    const na = Number.parseInt(a, 10);
    const nb = Number.parseInt(b, 10);
    if (Number.isFinite(na) && Number.isFinite(nb) && String(na) === a && String(nb) === b) {
        return na - nb;
    }
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

export interface CreateRackRequest {
    companyNo: string;
    warehouseID: string;
    rackNumber: string;
    verticalCount: number;
    shelfCount: number;

    /**
     * Number of drawers per shelf location (4th dimension).
     * 0 = 3D rack (no drawers), >0 = 4D rack with drawers
     */
    drawerCount?: number;

    /**
     * Utvid/kopier-modus: hopp over bincodes som allerede finnes og opprett kun de manglende.
     * false/undefined = dagens oppførsel: én eksisterende bincode avviser hele opprettelsen.
     */
    skipExisting?: boolean;

    // Default values for all locations
    stcZoneID: string;
    binSizeNo: number;
    binTypeNo: number;
    resourceNo?: number;
    binGrpNo?: number;
    stcBinCatNo?: number;
    pickPriorityNo?: number;
    pickableYesNo?: boolean;
    reservationAllowed?: boolean;
    autoReplenishment?: boolean;
}

export interface CreateRackResponse {
    locationsCreated: number;
    createdBinCodes: string[];
    errors: string[];
}

export interface ValidateRackRequest {
    companyNo: string;
    warehouseID: string;
    rackNumber: string;
    verticalCount: number;
    shelfCount: number;
    stcZoneID?: string;

    /**
     * Number of drawers per shelf location (4th dimension).
     * 0 = 3D rack (no drawers), >0 = 4D rack with drawers
     */
    drawerCount?: number;
}

export interface ValidateRackResponse {
    isValid: boolean;
    existingBinCodes: string[];
    warnings: string[];
    locationsToCreate: number;
}

export interface FilterBinsRequest {
    companyNo: string;
    warehouseID: string;
    rackNumbers?: string[];
    binSizeNo?: number[];
    binTypeNo?: number[];
    resourceNo?: number[];
    stcZoneID?: string[];
    binGrpNo?: number[];
    stcBinCatNo?: number[];
    pickPriorityNo?: number[];
    pickableYesNo?: boolean;
    hasItemNo?: boolean;
    
    // Extended filter options
    putAwayPriorityNo?: number[];
    reservationAllowed?: boolean;
    autoReplenishment?: boolean;
    negativeReservationAllowed?: boolean;
    sharedBinYesNo?: boolean;
    sharedBinPreferEmptyBin?: boolean;
    replenishMinLevelFrom?: number;
    replenishMinLevelTo?: number;
    replenishMaxLevelFrom?: number;
    replenishMaxLevelTo?: number;
    customSortIDFrom?: number;
    customSortIDTo?: number;
    sharedBinMaxQuantityFrom?: number;
    sharedBinMaxQuantityTo?: number;
}

export interface FilterOptions {
    binSizeNo: number[];
    binTypeNo: number[];
    resourceNo: number[];
    stcZoneID: string[];
    binGrpNo: number[];
    stcBinCatNo: number[];
    pickPriorityNo: number[];
    
    // Extended filter options
    putAwayPriorityNo: number[];
}

export interface BulkUpdateBinsRequest {
    companyNo: string;
    warehouseID: string;
    binCodes: string[];

    /**
     * Optional source bin used in a "Copy properties" operation.
     * Sent to backend for audit logging only.
     */
    sourceBinCode?: string;

    // Only set fields to update
    binSizeNo?: number;
    binTypeNo?: number;
    resourceNo?: number;
    binGrpNo?: number;
    stcBinCatNo?: number;
    stcZoneID?: string;
    pickPriorityNo?: number;
    pickableYesNo?: boolean;
    reservationAllowed?: boolean;
    autoReplenishment?: boolean;
    itemNo?: string;
    
    // New fields for extended bulk update support
    replenishMinLevel?: number;
    replenishMaxLevel?: number;
    customSortID?: number;
    putAwayPriorityNo?: number;
    negativeReservationAllowed?: boolean;
    sharedBinYesNo?: boolean;
    sharedBinMaxQuantity?: number;
    sharedBinPreferEmptyBin?: boolean;
    movementTypeNo?: number;
    // Unntaksflagg lokasjonsskann (TW 36344429). Tri-state + "ikke rør":
    // undefined/tom = ikke oppdater, 'true'/'false' = sett verdi, 'inherit' = sett NULL (arv).
    barCodeConfirmLocation?: string;
}

export interface BulkOperationResponse {
    successCount: number;
    successfulBinCodes: string[];
    errors: string[];
}

/** Angre-data for én bin etter masseendring: før-verdiene for feltene som ble endret */
export interface BulkUndoEntry {
    binCode: string;
    /** Før-verdier (samme feltnavn som BulkUpdateBinsRequest). Kun felt med kjent, ikke-null før-verdi. */
    before: Partial<Omit<BulkUpdateBinsRequest, 'companyNo' | 'warehouseID' | 'binCodes' | 'sourceBinCode'>>;
}

/** Angre-data for siste masseendring (lagres klientside, overlever ikke sideskift) */
export interface BulkUndoData {
    timestamp: string;
    warehouseID: string;
    /** Feltnavn som ble oppdatert */
    fieldsUpdated: string[];
    entries: BulkUndoEntry[];
    /** Felt som ikke kan angres automatisk (før-verdi ukjent/null klientside) */
    nonRevertibleFields: string[];
}

export interface BulkDeleteBinsRequest {
    companyNo: string;
    warehouseID: string;
    binCodes: string[];
    forceDelete?: boolean;
}

// UI state types
export interface FilterState {
    binSizeNo: number[];
    binTypeNo: number[];
    resourceNo: number[];
    stcZoneID: string[];
    binGrpNo: number[];
    stcBinCatNo: number[];
    pickPriorityNo: number[];
    pickableYesNo: 'all' | 'yes' | 'no';
    itemNo: 'all' | 'has' | 'empty';
    
    // Extended filter state
    putAwayPriorityNo: number[];
    reservationAllowed: 'all' | 'yes' | 'no';
    autoReplenishment: 'all' | 'yes' | 'no';
    negativeReservationAllowed: 'all' | 'yes' | 'no';
    sharedBinYesNo: 'all' | 'yes' | 'no';
    sharedBinPreferEmptyBin: 'all' | 'yes' | 'no';
    replenishMinLevelFrom: number;
    replenishMinLevelTo: number;
    replenishMaxLevelFrom: number;
    replenishMaxLevelTo: number;
    customSortIDFrom: number;
    customSortIDTo: number;
    sharedBinMaxQuantityFrom: number;
    sharedBinMaxQuantityTo: number;
}

export interface RackBuilderState {
    racks: RackSummary[];
    selectedRackNumbers: string[];
    allLocations: BinLocation[];
    filteredLocations: BinLocation[];
    selectedBinCodes: string[];
    filterOptions: FilterOptions | null;
    filterState: FilterState;
    isLoading: boolean;
    error: string | null;
}
